import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../i18n';
import { handleSectionClick } from '../utils/scrollToSection';

export const SAT1_VIDEO_ID = 'ON8llqfvFlk';
export const SAT1_WATCH_URL = `https://www.youtube.com/watch?v=${SAT1_VIDEO_ID}`;
export const SAT1_EMBED_URL = `https://www.youtube-nocookie.com/embed/${SAT1_VIDEO_ID}?autoplay=1&rel=0`;
/** The video's own YouTube thumbnail, served locally so no request leaves the site before play. */
export const SAT1_THUMBNAIL = '/sat1-report-thumbnail.jpg';

/**
 * The SAT.1 report on Ron's AI lessons at Realschule Miesbach. The cover shows
 * the YouTube thumbnail with a play button; the player itself is loaded from
 * YouTube only after the visitor presses play (privacy by default).
 * Anchor target for /#ai-for-kids (sitemap, llms.txt).
 */
export const VideoStory: React.FC = () => {
  const { t } = useLanguage();
  const [playing, setPlaying] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (playing) frameRef.current?.focus();
  }, [playing]);

  return (
    <figure className="lp-video-story" id="ai-for-kids">
      <div className="lp-video-stage" data-testid="video-stage">
        {playing ? (
          <iframe
            ref={frameRef}
            src={SAT1_EMBED_URL}
            title={t.video.iframeTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="lp-video-cover"
            onClick={() => setPlaying(true)}
            aria-label={t.video.playAria}
          >
            <img className="lp-video-thumb" src={SAT1_THUMBNAIL} alt="" width={1280} height={720} />
            <span className="lp-yt-play" aria-hidden="true">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>
      <figcaption>{t.video.caption}</figcaption>
      <p className="lp-video-disclosure">{t.video.disclosure}</p>
    </figure>
  );
};

/**
 * Hero: the slogan as headline, the company description, one invitation to meet
 * ContextFriend, and the SAT.1 classroom report next to it.
 */
const Hero: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="lp-hero lp-wrap" id="top" aria-labelledby="hero-heading">
      <div className="lp-hero-copy">
        <h1 id="hero-heading">
          {t.hero.headlineA}
          <br />
          <span className="lp-blue">{t.hero.headlineB}</span>
        </h1>
        <p className="lp-hero-lead">{t.hero.lead}</p>
        <p className="lp-hero-mission">{t.hero.mission}</p>
        <div className="lp-hero-actions">
          <a
            className="lp-button"
            href="#contextfriend"
            onClick={(event) => handleSectionClick(event, 'contextfriend')}
          >
            <span>{t.hero.cta}</span>
            <span aria-hidden="true">↓</span>
          </a>
        </div>
      </div>
      <VideoStory />
    </section>
  );
};

export default Hero;
